<template>
    <section id="case-section" class="mx-10" :class="context.isDarkMode ? 'bg-void' : 'bg-white'">

        <p class="text-center text-4xl xl:text-2xl 2xl:text-4xl font-semibold" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
            {{ translationHandler.translate('popular cases', 'capitalize') }}
        </p>

        <div class="flex gap-5 mx-10 justify-center mx-auto my-10 flex-col md:flex-row flex-wrap">
            <LoaderComponent 
                v-if="loadingProducts" 
                style="height: 300px;"
            />
            <div
                v-else
                v-for="(category, index) of categories"
                :key="index"
                :class="category.id == selectedCategory 
                    ? 'bg-third-color border-third-color text-silver' 
                    : `border border-silver ${context.isDarkMode ? 'text-silver' : 'text-void'}`
                "
                class="
                    hidden md:block
                    button-style w-full max-width-md rounded-lg px-4 shadow-lg text-center 
                    hover:text-silver hover:bg-third-color hover:border-third-color
                "
                @click="filterByCategory(category.id)"
            >
                {{ translationHandler.translate(category.title, 'capitalize') }}
            </div>

            <div v-if="true" class="flex flex-col md:hidden rounded-xl" :class="context.isDarkMode ? 'dark-mode bg-secondary-color text-white' : 'light-mode bg-white text-secondary-color'">
                <div class="flex-1">
                    <ul class="space-y-10 px-5 py-5">
                        <li v-for="(category, index) of categories" :key="index">
                            <div 
                                class="text-4xl flex justify-between w-full align-middle cursor-pointer border rounded-lg p-2"
                                :class="category.id == selectedCategory 
                                    ? 'bg-third-color border-third-color text-silver' 
                                    : `border border-silver ${context.isDarkMode ? 'text-silver' : 'text-void'}`
                                "    
                                @click="filterByCategory(category.id)"
                            >
                                <div class="my-auto flex">
                                    <p class="text-gray-700 ml-3 my-auto text-lg font-medium">
                                        {{ translationHandler.translate(category.title, 'ucfirst') }}
                                    </p>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="card">
            <DragCarrousel 
                :items="filteredProducts" 
                :actionButton="{
                    title: 'visit page', 
                    icon: 'pi pi-link'
                }" 
                @selected="handleCarrouselSelectedItem" 
                @clicked="handleClickOnButton" 
            />
        </div>
    </section>
</template>

<script setup>
import { useContextStore } from "@/stores/context";
import { ref, defineEmits, onMounted } from "vue";
import DragCarrousel from "./DragCarrousel.vue";
import urlHandler from "@/composables/urlHandler";
import translationHandler from "@/composables/handlers/TranslationHandler";
import LoaderComponent from "./Utils/LoaderComponent.vue";

import nlfinanceiraDescription from "@/assets/files/nlfinanceira.json";
import neuromindDescription from "@/assets/files/neuromind.json";
import gomesbritoadvocaciaDescription from "@/assets/files/gomesbritoadvocacia.json";
import oralisStudioDescription from "@/assets/files/oralisstudio.json";

const context = useContextStore();
const emit = defineEmits(['selectedCase']);

onMounted(() => {
    filterByCategory(selectedCategory.value);
});

const loadingProducts = ref(false);
const selectedCategory = ref('all');
const categories = ref([
    {id: 'all', title: 'all', bg: '#6a00bd'},
    {id: 'landing_page', title: 'landing page', bg: '#8f00ff'},
    {id: 'web_site', title: 'website', bg: '#6a00bd'},
    {id: 'law', title: 'law', bg: '#4a4e69'},
    {id: 'dentistry', title: 'dentistry', bg: '#0553CA'}
    // {id: 'ecommerce', title: 'E-commmerce', bg: '#6f5ea7'}
]);

const filteredProducts = ref([]);
const products = ref([
    {
        id: '1',
        title: 'NL Financeira',
        titleColor: '#53b669',
        description: 'Transforme Sua Vida Financeira Agora Mesmo!',
        fullDescription: nlfinanceiraDescription,
        image: require('@/assets/images/cases/nlfinanceira/preview-1.png'),
        url: 'https://nlfinanceira.cervodigital.com.br/',
        tags: [
            getCategoryTagById('landing_page')
        ],
        gallery: [
            {
                itemImageSrc: require('@/assets/images/cases/nlfinanceira/preview-1.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/nlfinanceira/thumbnail-1.png'), 
                alt: 'homepage'
            },
            {
                itemImageSrc: require('@/assets/images/cases/nlfinanceira/preview-2.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/nlfinanceira/thumbnail-2.png'), 
                alt: 'about'
            },
            {
                itemImageSrc: require('@/assets/images/cases/nlfinanceira/preview-3.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/nlfinanceira/thumbnail-3.png'),  
                alt: 'lawers team'
            },
            {
                itemImageSrc: require('@/assets/images/cases/nlfinanceira/preview-4.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/nlfinanceira/thumbnail-4.png'),  
                alt: 'services'
            },
            {
                itemImageSrc: require('@/assets/images/cases/nlfinanceira/preview-5.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/nlfinanceira/thumbnail-5.png'),  
                alt: 'about and testimony'
            },
            {
                itemImageSrc: require('@/assets/images/cases/nlfinanceira/preview-6.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/nlfinanceira/thumbnail-6.png'),  
                alt: 'contact and footer'
            },
        ]
    },
    {
        id: '2',
        title: 'Neuromind',
        titleColor: '#15e9c8',
        description: 'Potencialize Seu Negócio com Inteligência Artifícial!',
        fullDescription: neuromindDescription,
        image: require('@/assets/images/cases/neuromind/preview-1.png'),
        url: 'https://neuromind.cervodigital.com.br/',
        tags: [
            getCategoryTagById('landing_page'),
            getCategoryTagById('web_site')
        ],
        gallery: [
            {
                itemImageSrc: require('@/assets/images/cases/neuromind/preview-1.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/neuromind/thumbnail-1.png'), 
                alt: 'homepage'
            },
            {
                itemImageSrc: require('@/assets/images/cases/neuromind/preview-2.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/neuromind/thumbnail-2.png'), 
                alt: 'about'
            },
            {
                itemImageSrc: require('@/assets/images/cases/neuromind/preview-3.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/neuromind/thumbnail-3.png'),  
                alt: 'testimony'
            },
            {
                itemImageSrc: require('@/assets/images/cases/neuromind/preview-4.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/neuromind/thumbnail-4.png'),  
                alt: 'faq and footer'
            }
        ]
    },
    {
        id: '3',
        title: 'Gomes & Brito Advocacia',
        titleColor: '#cdc3a0',
        description: 'Seu Direito é Nossa Prioridade: Soluções Jurídicas Confiáveis!',
        fullDescription: gomesbritoadvocaciaDescription,
        image: require('@/assets/images/cases/gomesbritoadvocacia/preview-1.png'),
        url: 'https://gomesbritoadvocacia.alwaysdata.net/',
        tags: [
            getCategoryTagById('landing_page'),
            getCategoryTagById('web_site'),
            getCategoryTagById('law')
        ],
        gallery: [
            {
                itemImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/preview-1.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/thumbnail-1.png'), 
                alt: 'homepage'
            },
            {
                itemImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/preview-2.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/thumbnail-2.png'), 
                alt: 'about'
            },
            {
                itemImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/preview-3.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/thumbnail-3.png'),  
                alt: 'lawers team'
            },
            {
                itemImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/preview-4.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/thumbnail-4.png'),  
                alt: 'services'
            },
            {
                itemImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/preview-5.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/thumbnail-5.png'),  
                alt: 'about and testimony'
            },
            {
                itemImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/preview-6.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/gomesbritoadvocacia/thumbnail-6.png'),  
                alt: 'contact and footer'
            },
        ]
    },
    {
        id: '4',
        title: 'Oralis Studio',
        titleColor: '#6f96d1',
        description: 'Mais do que estética: saúde bucal completa',
        fullDescription: oralisStudioDescription,
        image: require('@/assets/images/cases/oralisStudio/preview-1.png'),
        url: 'https://oralisstudio.alwaysdata.net/',
        tags: [
            getCategoryTagById('landing_page'),
            getCategoryTagById('web_site'),
            getCategoryTagById('dentistry')
        ],
        gallery: [
            {
                itemImageSrc: require('@/assets/images/cases/oralisStudio/preview-1.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/oralisStudio/thumbnail-1.png'), 
                alt: 'homepage'
            },
            {
                itemImageSrc: require('@/assets/images/cases/oralisStudio/preview-2.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/oralisStudio/thumbnail-2.png'), 
                alt: 'about'
            },
            {
                itemImageSrc: require('@/assets/images/cases/oralisStudio/preview-3.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/oralisStudio/thumbnail-3.png'),  
                alt: 'promotion'
            },
            {
                itemImageSrc: require('@/assets/images/cases/oralisStudio/preview-4.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/oralisStudio/thumbnail-4.png'),  
                alt: 'specialities'
            },
            {
                itemImageSrc: require('@/assets/images/cases/oralisStudio/preview-5.png'), 
                thumbnailImageSrc: require('@/assets/images/cases/oralisStudio/thumbnail-5.png'),  
                alt: 'contact'
            },
        ]
    }
]);

function handleCarrouselSelectedItem(selectedItem) {
    console.log('se', selectedItem)
    emit('selectedItem', selectedItem);
}

function handleClickOnButton(args) {
    urlHandler.openLink(args.item.url ?? '');
}

function getCategoryTagById(id = '') {
    for (let index of Object.keys(categories.value)) {
        const category = categories.value[index];

        if (category.id == id) {
            return category;
        }
    }

    return null;
}

function filterByCategory(id = '') {
    const category = getCategoryTagById(id);
    if (!category) {
        return;
    }

    selectedCategory.value = id;
    filteredProducts.value = [];
    loadingProducts.value = true;

    if (id == 'all') {
        filteredProducts.value = products.value;
    } else {
        for (let product of products.value) {

            const productTags = product.tags ?? null;

            if (productTags) {
                for (let prodTag of productTags) {
                    if (prodTag.id == id) {
                        filteredProducts.value.push(product);
                    }
                }
            } 
        }
    }

    loadingProducts.value = false;
}

</script>
